import { useNavigate } from 'react-router';
import { Box, Grid, Skeleton, Container, Typography, Fade, Stack, Button } from '@mui/material';
import { EastRounded, ManageAccountsRounded } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { fadeUp } from './utils/dashboardUtils';
import AccountsHeader from './components/AccountsHeader';
import AccountCard from './components/AccountCard';
import { useAccountsDashboard } from './hooks/useAccountsDashboard';

const AccountsLandingPage = () => {
    const navigate = useNavigate();
    const { loading, accounts, dateRange, handleDateChange, refreshData } = useAccountsDashboard();

    return (
        <Box
            sx={{
                minHeight: '100vh',
                pb: 6,
                background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'
            }}
        >
            <Container maxWidth="xl" sx={{ mt: 0 }}>
                <AccountsHeader dateRange={dateRange} onDateChange={handleDateChange} onRefresh={refreshData} />

                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 3.5, px: 0.5 }}
                    >
                        <Box>
                            <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ letterSpacing: '-0.01em' }}>
                                Role-wise Net Totals
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Consolidated financial performance and net positions across organizational roles.
                            </Typography>
                        </Box>
                        <Button
                            onClick={() => navigate('/accounts/manage-double-entries')}
                            variant="contained"
                            size="large"
                            startIcon={<ManageAccountsRounded />}
                            endIcon={<EastRounded />}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 800,
                                px: 3,
                                py: 1.2,
                                bgcolor: '#fff',
                                color: '#1e3a8a',
                                border: '1px solid',
                                borderColor: 'rgba(30, 58, 138, 0.1)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: '#f8fafc',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                                },
                                transition: 'all 0.2s'
                            }}
                        >
                            Transaction Management
                        </Button>
                    </Stack>
                </motion.div>

                <Grid container spacing={3}>
                    {loading
                        ? Array.from(new Array(8)).map((_, index) => (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                  <Skeleton variant="rectangular" height={220} sx={{ borderRadius: '16px', bgcolor: 'rgba(0,0,0,0.03)' }} />
                              </Grid>
                          ))
                        : accounts.map((item, index) => (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                                  <AccountCard item={item} index={index} />
                              </Grid>
                          ))}
                </Grid>

                {!loading && accounts.length === 0 && (
                    <Fade in timeout={600}>
                        <Box sx={{ mt: 8, textAlign: 'center' }}>
                            <Typography variant="h4" color="text.secondary" fontWeight={500}>
                                No account records found for this period
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Try adjusting your date range or contact support if the issue persists.
                            </Typography>
                        </Box>
                    </Fade>
                )}
            </Container>
        </Box>
    );
};

export default AccountsLandingPage;
